import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, STAGES, LEAD_SOURCES, formatDateTime } from "./crmApi";
import "./Crm.css";

const STATUS_STYLE = {
  draft:     { label: "Draft",     color: "#6b7280", icon: "📝" },
  scheduled: { label: "Scheduled", color: "#3b82f6", icon: "🕐" },
  sending:   { label: "Sending",   color: "#f59e0b", icon: "📤" },
  paused:    { label: "Paused",    color: "#8b5cf6", icon: "⏸" },
  completed: { label: "Sent",      color: "#10b981", icon: "✅" },
  failed:    { label: "Failed",    color: "#dc2626", icon: "⚠️" },
};
const statusOf = (k) => STATUS_STYLE[k] || STATUS_STYLE.draft;

const BLANK = {
  name: "",
  message_kind: "template",
  template_name: "",
  template_language: "en_US",
  body: "",
  audience: { stages: [], sources: [], booked_only: false, quiet_days: 0 },
};

/**
 * WhatsApp campaigns: choose an audience from the CRM, write one message, and
 * send to everyone at once instead of opening WhatsApp per contact.
 */
function CrmCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [waStatus, setWaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);

  const [builder, setBuilder] = useState(null); // null closed, {} new, {...} edit
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/campaigns");
      setCampaigns(d.campaigns || []);
      crmFetch("/api/crm/whatsapp/status").then(setWaStatus).catch(() => {});
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // A sending campaign's counters move on their own; poll while one is live.
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "sending")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [campaigns, load]);

  // Audience size is the number that decides whether to press send, so it
  // updates as the filters change rather than only on save.
  useEffect(() => {
    if (builder === null) return;
    let cancelled = false;
    const t = setTimeout(() => {
      crmFetch("/api/crm/campaigns/audience-preview", {
        method: "POST",
        body: { audience: form.audience },
      })
        .then((d) => { if (!cancelled) setPreview(d); })
        .catch(() => {});
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [builder, form.audience]);

  const openNew = () => { setForm(BLANK); setError(""); setPreview(null); setBuilder({}); };

  const openEdit = (c) => {
    setForm({
      name: c.name,
      message_kind: c.message_kind,
      template_name: c.template_name || "",
      template_language: c.template_language || "en_US",
      body: c.body || "",
      audience: { ...BLANK.audience, ...(c.audience || {}) },
    });
    setError("");
    setBuilder(c);
  };

  const toggleIn = (key, value) =>
    setForm((f) => {
      const list = f.audience[key] || [];
      return {
        ...f,
        audience: {
          ...f.audience,
          [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
        },
      };
    });

  const save = async () => {
    if (!form.name.trim()) { setError("Give the campaign a name."); return; }
    if (form.message_kind === "template" && !form.template_name.trim()) {
      setError("Enter the template name exactly as approved in Meta Business Manager.");
      return;
    }
    if (form.message_kind === "text" && !form.body.trim()) {
      setError("Write the message."); return;
    }
    setSaving(true);
    setError("");
    try {
      if (builder.id) {
        await crmFetch(`/api/crm/campaigns/${builder.id}`, { method: "PUT", body: form });
      } else {
        await crmFetch("/api/crm/campaigns", { method: "POST", body: form });
      }
      setBuilder(null);
      load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const send = async (c, scheduledAt) => {
    const who = c.total_recipients || preview?.count || "the selected";
    if (!window.confirm(
      scheduledAt
        ? `Schedule "${c.name}" to send to ${who} contacts?`
        : `Send "${c.name}" now to ${who} contacts? This cannot be undone.`
    )) return;
    try {
      await crmFetch(`/api/crm/campaigns/${c.id}/send`, {
        method: "POST",
        body: { scheduled_at: scheduledAt || null },
      });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const pause = async (c) => {
    try {
      await crmFetch(`/api/crm/campaigns/${c.id}/pause`, { method: "POST" });
      load();
    } catch (e) { window.alert(e.message); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await crmFetch(`/api/crm/campaigns/${c.id}`, { method: "DELETE" });
      load();
    } catch (e) { window.alert(e.message); }
  };

  const openDetail = async (c) => {
    try {
      setDetail(await crmFetch(`/api/crm/campaigns/${c.id}`));
    } catch (e) { window.alert(e.message); }
  };

  const progress = (c) =>
    c.total_recipients > 0
      ? Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100)
      : 0;

  return (
    <CrmPage
      title="📣 WhatsApp Campaigns"
      subtitle="Send, automate & engage customers at scale"
      loading={loading}
      planBlocked={planBlocked}
      actions={<button className="crm-primary-btn" onClick={openNew}>＋ New Campaign</button>}
    >
      {waStatus && !waStatus.configured && (
        <div className="crm-form-error" style={{ marginBottom: 16 }}>
          WhatsApp isn't connected on this server yet, so campaigns can be drafted but not
          sent. Add your Meta Cloud API phone number ID and access token to enable sending.
        </div>
      )}

      <div className="crm-card crm-wa-note">
        <strong>Before your first campaign:</strong> WhatsApp only lets a business start a
        conversation with an <em>approved message template</em>. Free-form text reaches a
        contact only if they messaged you in the last 24&nbsp;hours. Create your template in
        Meta Business Manager, then enter its exact name below — its <code>{"{{1}}"}</code>{" "}
        becomes the client's name and <code>{"{{2}}"}</code> your studio name.
      </div>

      {campaigns.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">📣</span>
          No campaigns yet.
          <div className="crm-empty-hint">
            Re-engage past clients for a festival offer, or tell every booked couple about
            your new album — one message, your whole list.
          </div>
        </div>
      ) : (
        campaigns.map((c) => {
          const st = statusOf(c.status);
          return (
            <div key={c.id} className="crm-card crm-camp-card">
              <div className="crm-camp-head">
                <div style={{ minWidth: 0 }}>
                  <div className="crm-row-title">{c.name}</div>
                  <div className="crm-row-sub">
                    {c.message_kind === "template"
                      ? `Template: ${c.template_name} (${c.template_language})`
                      : "Free-form text"}
                    {c.scheduled_at ? ` · scheduled ${formatDateTime(c.scheduled_at)}` : ""}
                  </div>
                </div>
                <span className="crm-badge" style={{ background: `${st.color}22`, color: st.color }}>
                  {st.icon} {st.label}
                </span>
              </div>

              {c.total_recipients > 0 && (
                <>
                  <div className="crm-camp-bar">
                    <div className="crm-camp-fill" style={{ width: `${progress(c)}%` }} />
                  </div>
                  <div className="crm-row-sub">
                    {c.sent_count} sent
                    {c.failed_count > 0 && <span style={{ color: "#dc2626" }}> · {c.failed_count} failed</span>}
                    {" "}of {c.total_recipients}
                  </div>
                </>
              )}

              <div className="crm-quote-card-actions">
                {["draft", "scheduled", "paused"].includes(c.status) && (
                  <button className="crm-btn primary" onClick={() => send(c, null)}>
                    {c.status === "paused" ? "▶ Resume" : "📤 Send now"}
                  </button>
                )}
                {["sending", "scheduled"].includes(c.status) && (
                  <button className="crm-btn ghost" onClick={() => pause(c)}>⏸ Pause</button>
                )}
                {["draft", "scheduled"].includes(c.status) && (
                  <button className="crm-btn ghost" onClick={() => openEdit(c)}>✏️ Edit</button>
                )}
                {c.total_recipients > 0 && (
                  <button className="crm-btn ghost" onClick={() => openDetail(c)}>👁 Recipients</button>
                )}
                {c.status !== "sending" && (
                  <button className="crm-btn danger" onClick={() => remove(c)}>Delete</button>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* ── Builder ── */}
      {builder !== null && (
        <div className="crm-modal-backdrop" onClick={() => setBuilder(null)}>
          <div className="crm-modal crm-quote-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{builder.id ? "Edit campaign" : "＋ New campaign"}</h3>
            {error && <div className="crm-form-error">{error}</div>}

            <div className="crm-field full">
              <label>Campaign name (only you see this)</label>
              <input value={form.name} autoFocus
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     placeholder="e.g. Diwali offer 2026" />
            </div>

            <h4 className="crm-section-title" style={{ marginTop: 18 }}>Who receives it</h4>
            <div className="crm-field full">
              <label>Pipeline stage (any lead of theirs)</label>
              <div className="crm-stage-row">
                {STAGES.map((s) => (
                  <button key={s.key} type="button"
                    className={`crm-stage-chip ${form.audience.stages.includes(s.key) ? "active" : ""}`}
                    style={form.audience.stages.includes(s.key) ? { background: s.color } : {}}
                    onClick={() => toggleIn("stages", s.key)}>
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="crm-field full" style={{ marginTop: 10 }}>
              <label>Where they came from</label>
              <div className="crm-stage-row">
                {LEAD_SOURCES.map((s) => (
                  <button key={s} type="button"
                    className={`crm-stage-chip ${form.audience.sources.includes(s) ? "active" : ""}`}
                    style={form.audience.sources.includes(s) ? { background: "#a83c90" } : {}}
                    onClick={() => toggleIn("sources", s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="crm-form-grid" style={{ marginTop: 10 }}>
              <div className="crm-field">
                <label className="crm-checkline">
                  <input type="checkbox" checked={form.audience.booked_only}
                         onChange={(e) => setForm((f) => ({
                           ...f, audience: { ...f.audience, booked_only: e.target.checked },
                         }))} />
                  Only clients who have booked
                </label>
              </div>
              <div className="crm-field">
                <label>Skip anyone messaged in the last…</label>
                <select value={form.audience.quiet_days}
                        onChange={(e) => setForm((f) => ({
                          ...f, audience: { ...f.audience, quiet_days: Number(e.target.value) },
                        }))}>
                  <option value={0}>No limit</option>
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
            </div>

            <div className="crm-camp-preview">
              {preview
                ? <><strong>{preview.count}</strong> contact{preview.count === 1 ? "" : "s"} match
                    {preview.sample.length > 0 && (
                      <span className="crm-row-sub"> — {preview.sample.map((s) => s.name).join(", ")}
                        {preview.count > preview.sample.length ? "…" : ""}</span>
                    )}
                  </>
                : "Counting your audience…"}
            </div>

            <h4 className="crm-section-title" style={{ marginTop: 18 }}>What it says</h4>
            <div className="crm-stage-row">
              <button type="button"
                className={`crm-stage-chip ${form.message_kind === "template" ? "active" : ""}`}
                style={form.message_kind === "template" ? { background: "#10b981" } : {}}
                onClick={() => setForm((f) => ({ ...f, message_kind: "template" }))}>
                ✅ Approved template (recommended)
              </button>
              <button type="button"
                className={`crm-stage-chip ${form.message_kind === "text" ? "active" : ""}`}
                style={form.message_kind === "text" ? { background: "#f59e0b" } : {}}
                onClick={() => setForm((f) => ({ ...f, message_kind: "text" }))}>
                💬 Free text (24h window only)
              </button>
            </div>

            {form.message_kind === "template" ? (
              <div className="crm-form-grid" style={{ marginTop: 12 }}>
                <div className="crm-field">
                  <label>Template name</label>
                  <input value={form.template_name}
                         onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
                         placeholder="e.g. diwali_offer" />
                </div>
                <div className="crm-field">
                  <label>Language code</label>
                  <input value={form.template_language}
                         onChange={(e) => setForm((f) => ({ ...f, template_language: e.target.value }))}
                         placeholder="en_US" />
                </div>
                <div className="crm-field full">
                  <span className="crm-row-sub">
                    Must match a template already approved in Meta Business Manager.
                    We fill <code>{"{{1}}"}</code> with the client's name and{" "}
                    <code>{"{{2}}"}</code> with your studio name.
                  </span>
                </div>
              </div>
            ) : (
              <div className="crm-field full" style={{ marginTop: 12 }}>
                <label>Message</label>
                <textarea style={{ minHeight: 110 }} value={form.body}
                          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                          placeholder="Hi {{client_name}}, …" />
                <span className="crm-row-sub">
                  Only delivered to contacts who messaged you in the last 24 hours — everyone
                  else will show as failed. Supports <code>{"{{client_name}}"}</code> and{" "}
                  <code>{"{{studio_name}}"}</code>.
                </span>
              </div>
            )}

            <div className="crm-modal-actions">
              <button className="crm-btn ghost" onClick={() => setBuilder(null)}>Cancel</button>
              <button className="crm-btn primary" disabled={saving} onClick={save}>
                {saving ? "Saving…" : builder.id ? "Save changes" : "Save as draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recipients ── */}
      {detail && (
        <div className="crm-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="crm-modal crm-quote-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{detail.campaign.name} — recipients</h3>
            <div className="crm-row-sub" style={{ marginBottom: 12 }}>
              {detail.campaign.sent_count} sent · {detail.campaign.failed_count} failed ·{" "}
              {detail.campaign.total_recipients} total
            </div>
            <div className="crm-table-wrap" style={{ maxHeight: "56vh", overflowY: "auto" }}>
              <table className="crm-table">
                <thead><tr><th>Contact</th><th>Status</th></tr></thead>
                <tbody>
                  {detail.recipients.map((r) => (
                    <tr key={r.id} style={{ cursor: "default" }}>
                      <td>
                        <div className="cell-main">{r.name || "—"}</div>
                        <div className="cell-sub">{r.phone}</div>
                      </td>
                      <td>
                        <span className="crm-badge" style={{
                          background: r.status === "sent" ? "#d1fae5"
                            : r.status === "failed" ? "#fee2e2" : "#f1f1f4",
                          color: r.status === "sent" ? "#065f46"
                            : r.status === "failed" ? "#b91c1c" : "#666",
                        }}>{r.status}</span>
                        {r.error && <div className="cell-sub" style={{ color: "#b91c1c" }}>{r.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="crm-modal-actions">
              <button className="crm-btn ghost" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </CrmPage>
  );
}

export default CrmCampaigns;
